import { useCallback, useState, type DragEvent, type ChangeEvent } from "react";
import { Upload, X, FileText } from "lucide-react";
import { cn } from "@/lib/utils";

interface FileUploadProps {
  value?: File | null;
  onChange: (file: File | null) => void;
  accept?: string;
  maxSizeMb?: number;
  label?: string;
  hint?: string;
  className?: string;
}

export function FileUpload({
  value,
  onChange,
  accept = "image/*,application/pdf",
  maxSizeMb = 10,
  label = "Arrastra el archivo aquí o haz clic",
  hint,
  className,
}: FileUploadProps) {
  const [isOver, setIsOver] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleFile = useCallback(
    (file: File | undefined) => {
      setError(null);
      if (!file) return;
      const sizeMb = file.size / (1024 * 1024);
      if (sizeMb > maxSizeMb) {
        setError(`El archivo excede ${maxSizeMb}MB`);
        return;
      }
      onChange(file);
    },
    [maxSizeMb, onChange],
  );

  function onDrop(e: DragEvent<HTMLLabelElement>) {
    e.preventDefault();
    setIsOver(false);
    handleFile(e.dataTransfer.files[0]);
  }

  function onInput(e: ChangeEvent<HTMLInputElement>) {
    handleFile(e.target.files?.[0]);
  }

  if (value) {
    return (
      <div className={cn("card p-4 flex items-center gap-3", className)}>
        <FileText className="size-8 text-fg-muted shrink-0" />
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium truncate">{value.name}</p>
          <p className="text-xs text-fg-muted">
            {(value.size / 1024).toFixed(0)} KB
          </p>
        </div>
        <button
          type="button"
          onClick={() => onChange(null)}
          className="text-fg-muted hover:text-error"
          aria-label="Quitar archivo"
        >
          <X className="size-5" />
        </button>
      </div>
    );
  }

  return (
    <div className={className}>
      <label
        onDragOver={(e) => {
          e.preventDefault();
          setIsOver(true);
        }}
        onDragLeave={() => setIsOver(false)}
        onDrop={onDrop}
        className={cn(
          "flex flex-col items-center justify-center gap-2 p-6 border-2 border-dashed rounded-md cursor-pointer transition-colors",
          isOver ? "border-brand bg-brand/5" : "border-border hover:border-fg-muted",
        )}
      >
        <Upload className="size-6 text-fg-muted" />
        <span className="text-sm font-medium">{label}</span>
        {hint && <span className="text-xs text-fg-muted">{hint}</span>}
        <input type="file" accept={accept} onChange={onInput} className="hidden" />
      </label>
      {error && <p className="mt-1 text-xs text-error">{error}</p>}
    </div>
  );
}
